import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon";
import { cx } from "../lib/cx";

export interface ToastOptions {
  message: string;
  tone?: "success" | "danger" | "info";
  icon?: IconName;
  /** ทุกการลบหรือเปลี่ยนสถานะควรมี action "เลิกทำ" */
  action?: { label: string; onClick: () => void };
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
}

const ToastContext = createContext<((o: ToastOptions) => void) | null>(null);

/** อยู่เหนือแถบล่างบนมือถือ มุมล่างกลางบนเดสก์ท็อป aria-live="polite" */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (o: ToastOptions) => {
      const id = ++seq.current;
      setItems((xs) => [...xs, { ...o, id }]);
      window.setTimeout(() => dismiss(id), o.duration ?? 4000);
    },
    [dismiss],
  );

  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((it) => (
          <div key={it.id} className={cx("toast", it.tone && `toast-${it.tone}`)}>
            <Icon name={it.icon ?? (it.tone === "danger" ? "alert" : "checkCircle")} />
            <span className="toast-msg">{it.message}</span>
            {it.action && (
              <button
                type="button"
                onClick={() => {
                  it.action?.onClick();
                  dismiss(it.id);
                }}
              >
                {it.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (o: ToastOptions) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
